import { type Color, PAWN, QUEEN, TYPE_MASK, WHITE, cheb, colorBit, other, pieceColor } from '../board.ts';
import { FenError } from '../notation.ts';
import { type MoveContext, draw, rulesFor, win } from '../rules.ts';
import type { Position, Variant } from '../types.ts';
import { STANDARD_FEN } from './classic.ts';

// Day 13 (draft). The queen is a traitor. After every 7th move (counting both sides) every
// queen on the board switches sides. An odd period, so the swap alternates between coming
// after White's move and after Black's. A move that would leave your king attacked by a queen
// about to turn on you is illegal, like leaving it in check; with no other move you've been
// betrayed and lose.
//
// ext: [moves since the last swap (0–6)]

export const TRAITOR_EVERY = 7;
const COUNT = 0;

export const TRAITOR_FEN = `${STANDARD_FEN} 0`;

/** Moves until the queens switch sides (1 = right after the next move). */
export function swapIn(pos: { ext: Int32Array | null }): number {
  return TRAITOR_EVERY - (pos.ext?.[COUNT] ?? 0);
}

const scratch = new Int8Array(64);

/** The board with every queen's colour switched. */
export function swappedBoard(board: Int8Array, out: Int8Array = scratch): Int8Array {
  for (let sq = 0; sq < 64; sq++) {
    const p = board[sq];
    out[sq] = (p & TYPE_MASK) === QUEEN && pieceColor(p) !== -1 ? QUEEN | colorBit(other(pieceColor(p) as Color)) : p;
  }
  return out;
}

/** The side to move must keep its king out of reach of its own queens, which turn next move. */
export function inTraitorDanger(pos: Position): boolean {
  if (swapIn(pos) !== 1) return false;
  const k = pos.royals[pos.turn];
  return k >= 0 && rulesFor(traitor).isAttacked(swappedBoard(pos.board), k, other(pos.turn));
}

export const traitor: Variant = {
  id: 'traitor',
  info: {
    name: 'Traitor Chess',
    but: 'the queen is a traitor',
    tagline: 'Every 7 moves the queens switch sides. Never trust her.',
    summary: 'After every 7th move **every queen switches sides**. Your best piece is a double agent.',
    rules: [
      'After every 7th move (counting both players), every queen on the board switches sides.',
      'Promoted queens switch too.',
      'You may not leave your king where a queen that’s about to switch would attack it. No way out means you’ve been betrayed.',
      'Otherwise normal chess: checkmate wins; stalemate, repetition and the 50-move rule draw.',
    ],
    accent: '#ff8a3d',
    emoji: '🗡️',
    bots: ['Gossip', 'Spy', 'Double Agent', 'Mole', 'Brutus'],
  },
  startFen: TRAITOR_FEN,
  extSize: 1,
  // A queen can switch to a side with nothing else, so dead positions (and timeouts against a
  // side with nothing) need no queens at all, and no pawns that could promote to one.
  insufficientMaterial: (pos) => !pos.board.some((p) => (p & TYPE_MASK) === QUEEN || (p & TYPE_MASK) === PAWN),

  parseExtra(fields, pos) {
    const [count = '0'] = fields;
    const n = Number(count);
    if (!Number.isInteger(n) || n < 0 || n >= TRAITOR_EVERY) throw new FenError('Bad traitor counter');
    pos.ext![COUNT] = n;
  },

  formatExtra(pos) {
    return [String(pos.ext![COUNT])];
  },

  afterMove(ctx: MoveContext) {
    const { next } = ctx;
    const ext = next.ext!;
    ext[COUNT] = (ext[COUNT] + 1) % TRAITOR_EVERY;
    if (ext[COUNT] !== 0) return;
    const b = next.board;
    for (let sq = 0; sq < 64; sq++) {
      const p = b[sq];
      if ((p & TYPE_MASK) !== QUEEN || pieceColor(p) === -1) continue;
      const turned = QUEEN | colorBit(other(pieceColor(p) as Color));
      b[sq] = turned;
      ctx.fx('betray', sq);
      ctx.events?.push({ t: 'add', sq, piece: turned, cause: 'betray' });
    }
  },

  threatened: inTraitorDanger,

  noMoves(pos) {
    const rules = rulesFor(traitor);
    if (rules.inCheck(pos)) return win(other(pos.turn), 'checkmate');
    if (inTraitorDanger(pos)) return win(other(pos.turn), 'betrayed');
    return draw('stalemate');
  },

  evaluate(pos) {
    // A queen about to switch is worth less to her side, and more to the other.
    const n = swapIn(pos);
    const share = n <= 1 ? 1 : n <= 2 ? 0.5 : n <= 4 ? 0.2 : 0;
    if (n > 6) return 0;
    let score = 0;
    for (let sq = 0; sq < 64; sq++) {
      const p = pos.board[sq];
      if ((p & TYPE_MASK) !== QUEEN) continue;
      const c = pieceColor(p);
      if (c === -1) continue;
      const sign = c === WHITE ? -1 : 1;
      // Her value moves to the other side, and she'll be standing next to the king she
      // leaves: keep her away from your own king as the swap gets close.
      const k = pos.royals[c];
      const near = k >= 0 ? Math.max(0, 5 - cheb(sq, k)) : 0;
      score += sign * (1800 * share + near * 70 * Math.max(share, 0.3));
    }
    return score;
  },

  reasonText: {
    betrayed: 'Betrayed by the queen',
  },
};

